import axios from 'axios';

// Use a dedicated axios instance for agent calls so the global response
// interceptor registered in `api-configuration.js` does not wrap agent errors
// into a generic "An unexpected error occurred." body before this file's
// `explainScreenshotError` can see them. Axios interceptors are per-instance
// (each `Axios` instance owns its own `InterceptorManager`), so registering on
// the default `axios` does not affect this instance. Verified against
// node_modules/axios/lib/core/Axios.js (`this.interceptors = { request: new
// InterceptorManager(), response: new InterceptorManager() }`) and
// lib/axios.js (`axios.create = function create(instanceConfig) { return
// createInstance(mergeConfig(axios.defaults, instanceConfig)); }`) for the
// installed version (0.21.4, declared ^0.21.1).
const agentClient = axios.create();

// Exported for tests so MockAdapter can attach to the dedicated instance
// (and so tests can prove the global interceptor does not affect it). In
// production callers should use the request helpers above, not this client
// directly.
export { agentClient };

const AGENT_URL = process.env.REACT_APP_AGENT_URL || 'http://localhost:8000';

// The agent's own wording for "I read the file and chose not to draft an RFA
// from it" (api/routes/rfas.py: a skipped pipeline result). It is not a read or
// upload failure, so it gets its own sentence and a `declined` flag.
const DECLINED_PIPELINE = /pipeline (halted|did not create)/i;

const DECLINED_MESSAGE =
  "We couldn't make an RFA from that file. Try a clearer screenshot or a different file.";

const COULD_NOT_REACH_MESSAGE =
  "We couldn't reach the server. Check your connection and try again.";

function agentReason(data) {
  if (!data) return '';
  if (typeof data.detail === 'string') return data.detail;
  if (typeof data.message === 'string') return data.message;
  return '';
}

function isDeclined(error) {
  return Boolean(
    error &&
      error.response &&
      DECLINED_PIPELINE.test(agentReason(error.response.data))
  );
}

function explainScreenshotError(error) {
  if (!error || !error.response) {
    return COULD_NOT_REACH_MESSAGE;
  }

  if (isDeclined(error)) {
    return DECLINED_MESSAGE;
  }

  const data = error.response.data;

  if (data && typeof data.detail === 'string' && data.detail.length > 0) {
    return data.detail;
  }

  if (data && Array.isArray(data.detail)) {
    const items = data.detail
      .map((item) => {
        if (typeof item === 'string') return item;
        if (item && typeof item.msg === 'string') {
          return item.loc && item.loc.length > 0
            ? `${item.loc.join('.')}: ${item.msg}`
            : item.msg;
        }
        return null;
      })
      .filter(Boolean);
    if (items.length > 0) {
      return items.join('; ');
    }
  }

  if (data && typeof data.message === 'string' && data.message.length > 0) {
    return data.message;
  }

  if (data && typeof data.error === 'string' && data.error.length > 0) {
    return data.error;
  }
  if (data && Array.isArray(data.errors) && data.errors.length > 0) {
    const items = data.errors.filter(
      (e) => typeof e === 'string' && e.length > 0
    );
    if (items.length > 0) {
      return items.join('; ');
    }
  }

  return 'The server could not create the RFA from that image.';
}

/**
 * Upload a screenshot and request a draft RFA be created from its contents.
 *
 * @param {number} projectId
 * @param {File}   file          - image or PDF file from a file input / drop zone
 * @param {string} accessToken   - Devise access-token header value
 * @param {string} client        - Devise client header value
 * @param {string} uid           - Devise uid (current user's email)
 * @param {string} [message]     - Optional typed request sent with the file (the agent caps it at 2000 characters)
 * @returns {Promise<{ok: boolean, data?: object, errors?: string[], status?: number|null, declined?: boolean}>}
 */
function createRfaFromScreenshot(
  projectId,
  file,
  accessToken,
  client,
  uid,
  message
) {
  const formData = new FormData();
  formData.append('project_id', projectId);
  formData.append('uid', uid);
  formData.append('file', file);
  const typedMessage = typeof message === 'string' ? message.trim() : '';
  if (typedMessage) {
    formData.append('message', typedMessage);
  }

  return agentClient
    .post(`${AGENT_URL}/rfas/from-screenshot`, formData, {
      headers: {
        'access-token': accessToken,
        client: client,
        uid: uid,
      },
    })
    .then((response) => ({ ok: true, data: response.data }))
    .catch((error) => ({
      ok: false,
      errors: [explainScreenshotError(error)],
      // HTTP status of the failure, or null when the agent was not reached.
      status: error && error.response ? error.response.status : null,
      // True when the agent read the file and declined to draft an RFA from it.
      declined: isDeclined(error),
    }));
}

/**
 * Upload one or more files and request draft submittals be extracted from them.
 *
 * @param {number}   projectId
 * @param {File[]}   files         - PDF / DOCX / image files
 * @param {string}   accessToken   - Devise access-token header value
 * @param {string}   client        - Devise client header value
 * @param {string}   uid           - Devise uid (current user's email)
 * @returns {Promise<{ok: boolean, data?: object, errors?: string[]}>}
 */
function analyzeSubmittalFiles(projectId, files, accessToken, client, uid) {
  const formData = new FormData();
  formData.append('project_id', projectId);
  formData.append('uid', uid);
  files.forEach((file) => formData.append('files', file));

  return agentClient
    .post(`${AGENT_URL}/submittals/from-files`, formData, {
      headers: {
        'access-token': accessToken,
        client: client,
        uid: uid,
      },
    })
    .then((response) => ({ ok: true, data: response.data }))
    .catch((error) => {
      const detail =
        error && error.response && error.response.data
          ? error.response.data.detail
          : null;

      if (Array.isArray(detail)) {
        return {
          ok: false,
          errors: detail.map((item) =>
            typeof item === 'string' ? item : item.msg || 'Upload failed'
          ),
        };
      }
      if (typeof detail === 'string' && detail.length > 0) {
        return { ok: false, errors: [detail] };
      }
      return { ok: false, errors: ['Failed to analyze submittal files.'] };
    });
}

export { createRfaFromScreenshot, analyzeSubmittalFiles };
